import React from 'react';
import { HelpCircle, ExternalLink, BookOpen, CheckCircle2, Wrench } from 'lucide-react';
import { AtomicStep } from '../../types';

interface ExplanationCardProps {
  step: AtomicStep;
}

export const ExplanationCard: React.FC<ExplanationCardProps> = ({ step }) => {
  return (
    <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
      {/* Header Info & External Tool Link */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px', borderBottom: '1px solid #1e293b', paddingBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '10px', fontWeight: 800, color: '#f43f5e', background: 'rgba(244, 63, 94, 0.12)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
            ZYNEX STANDARD
          </span>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', background: '#070a12', border: '1px solid #1e293b', padding: '3px 10px', borderRadius: '6px', fontWeight: 600 }}>
            <Wrench size={13} color="#38bdf8" />
            <span>TOOL: <strong style={{ color: '#ffffff' }}>{step.tool}</strong></span>
          </div>

          {step.toolUrl && (
            <a
              href={step.toolUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '12px',
                color: '#38bdf8',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                textDecoration: 'none',
                fontWeight: 700,
                background: 'rgba(2, 132, 199, 0.15)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                padding: '3px 10px',
                borderRadius: '6px',
                transition: 'all 0.15s ease',
              }}
            >
              <span>Open Tool</span>
              <ExternalLink size={12} />
            </a>
          )}
        </div>

        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'var(--font-mono)' }}>
          SCOPE: {step.coverage.toUpperCase()}
        </span>
      </div>

      {/* WHAT ARE WE DOING? */}
      <div style={{ marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>
          <HelpCircle size={14} />
          <span>What Are We Doing?</span>
        </div>
        <p style={{ fontSize: '15px', color: '#ffffff', margin: 0, fontWeight: 600, lineHeight: 1.5 }}>
          {step.what}
        </p>
      </div>

      {/* WHY ARE WE DOING IT? */}
      <div style={{ marginBottom: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>
          <BookOpen size={14} />
          <span>Why Are We Doing It?</span>
        </div>
        <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
          {step.why}
        </p>
      </div>

      {/* DO THIS NOW (EXACT ACTIONABLE STEPS) */}
      <div
        style={{
          background: '#070a12',
          borderRadius: '12px',
          padding: '18px 20px',
          border: '1px solid #1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>
          <CheckCircle2 size={14} />
          <span>Do This Now — Step-by-Step Instructions</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {step.how.map((instruction, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '2px 6px', borderRadius: '4px', fontFamily: 'var(--font-mono)', minWidth: '24px', textAlign: 'center' }}>
                0{idx + 1}
              </span>
              <span style={{ fontSize: '13.5px', color: '#f8fafc', lineHeight: 1.5 }}>
                {instruction}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
